// AudioRecorderSection Component - UI for audio recording and management
import React, { useCallback } from 'react';
import { Button, Icon, Image, Segment, Message, Label } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import microphoneShield from '../../../../assets/microphone-shield.svg';
import microphoneAndFile from '../../../../assets/microphoneAndFile.svg';

const AudioRecorderSection = ({
  audioManager,
  isCreatingNew = false,
  onAudioChange = () => { }
}) => {
  const { state, actions, helpers, computed } = audioManager;

  // Debug logging
  console.log('[AUDIO RECORDER SECTION] State received:', state);
  console.log('[AUDIO RECORDER SECTION] Computed values:', computed);
  console.log('[AUDIO RECORDER SECTION] isCreatingNew:', isCreatingNew);

  const {
    isRecording,
    audioURL,
    recordingDuration,
    audioSource,
    uploadedAudioName,
    uploadedAudioSize,
    signedVoiceUrl,
    replacingAudio,
    audioError
  } = state;

  const {
    startRecording,
    stopRecording,
    deleteAudio,
    replaceAudio,
    handleAudioUpload,
    setAudioError
  } = actions;

  const {
    formatDuration,
    formatFileSize,
    FILE_LIMITS
  } = helpers;

  const {
    hasAudio,
    isExistingAudio
  } = computed;

  // Handle dropped audio file
  const onDrop = useCallback(acceptedFiles => {
    if (acceptedFiles.length === 0) {
      setAudioError('Invalid file type. Please upload MP3, WAV, OGG, M4A, MOV, or WEBM files.');
      return;
    }

    const file = acceptedFiles[0];

    // Check file size based on file type
    const extension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
    const fileConfig = FILE_LIMITS.video.extensions.includes(extension)
      ? FILE_LIMITS.video
      : FILE_LIMITS.audio;

    if (file.size > fileConfig.maxSize) {
      const actualSize = formatFileSize(file.size);
      setAudioError(`File size too large (${actualSize}). Maximum size is ${fileConfig.label}.`);
      return;
    }

    const url = URL.createObjectURL(file);

    // Extract duration from audio file
    const audio = new Audio(url);
    audio.addEventListener('loadedmetadata', () => {
      handleAudioUpload(file, url, audio.duration);
      onAudioChange({ hasAudio: true, blob: file, source: 'upload' });
    });

    audio.addEventListener('error', () => {
      setAudioError('Failed to load audio file. Please check the file format.');
      URL.revokeObjectURL(url);
    });
  }, [handleAudioUpload, setAudioError, formatFileSize, onAudioChange, FILE_LIMITS.audio, FILE_LIMITS.video]);

  // Setup dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/*': ['.mp3', '.wav', '.ogg', '.m4a', '.webm'],
      'video/quicktime': ['.mov']
    },
    disabled: hasAudio && !replacingAudio,
    maxFiles: 1,
    multiple: false
  });

  // Handle recording state changes
  const handleStartRecording = async () => {
    await startRecording();
    onAudioChange({ hasAudio: false, hasNewAudio: false, isRecording: true });
  };

  const handleStopRecording = () => {
    stopRecording();
    // After recording stops, we have new audio
    onAudioChange({ hasAudio: true, hasNewAudio: true, source: 'recording' });
  };

  const handleDeleteAudio = () => {
    deleteAudio();
    onAudioChange({ hasAudio: false, hasNewAudio: false });
  };

  const handleReplaceAudio = () => {
    replaceAudio();
    // When replacing, we're clearing audio but not marking as new yet
    onAudioChange({ hasAudio: false, hasNewAudio: false, replacing: true });
  };

  return (
    <div className="recording-section">
      <h3>
        <Icon name="microphone" />
        Script & Recording
      </h3>

      {audioError && (
        <Message negative>
          <Message.Header>Recording Error</Message.Header>
          <p>{audioError}</p>
        </Message>
      )}

      {/* Existing Audio Section - Always visible in edit mode with existing audio */}
      {!isCreatingNew && isExistingAudio && !replacingAudio && (
        <Segment color="green">
          <h4>
            <Icon name="volume up" />
            Current Recording
          </h4>
          <div className="existing-audio-section">
            <Label color="green">
              <Icon name="check circle" />
              This character has an existing recording
            </Label>
            <div className="audio-player-section" style={{ marginTop: '15px' }}>
              {signedVoiceUrl ? (
                <audio
                  controls
                  src={signedVoiceUrl}
                  style={{ width: '100%' }}
                  preload="metadata"
                  onError={(e) => {
                    console.error('[AUDIO ERROR] Failed to load audio:', e);
                    setAudioError(`Unable to load audio file. Please try again.`);
                  }}
                  onLoadedMetadata={(e) => {
                    console.log('[AUDIO] Successfully loaded metadata');
                    console.log('[AUDIO] Duration:', e.target.duration);
                    setAudioError(null);
                  }}
                />
              ) : (
                <div style={{ padding: '10px', textAlign: 'center', color: '#666' }}>
                  <Icon name="spinner" loading />
                  Loading audio...
                </div>
              )}
              {uploadedAudioName && (
                <div style={{ marginTop: '10px', color: '#666' }}>
                  <Icon name="file audio" />
                  File: {uploadedAudioName}
                </div>
              )}
              {recordingDuration > 0 && (
                <div className="duration-display" style={{ marginTop: '5px' }}>
                  <Icon name="clock" />
                  Duration: {formatDuration(recordingDuration)}
                </div>
              )}
            </div>
            <Button
              type="button"
              color="orange"
              basic
              icon
              labelPosition="left"
              onClick={handleReplaceAudio}
              style={{ marginTop: '15px' }}
            >
              <Icon name="exchange" />
              Replace Audio
            </Button>
          </div>
        </Segment>
      )}

      {/* Show recording/upload sections only when creating new or replacing audio */}
      {(isCreatingNew || replacingAudio || !isExistingAudio) && (
        <>
          <Segment>
            <h4>
              <Icon name="microphone" />
              Record Voice
            </h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <Image src={microphoneShield} size="tiny" style={{ flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div className="recording-controls">
                  {!isRecording ? (
                    <Button
                      type="button"
                      color="red"
                      icon
                      labelPosition="left"
                      onClick={handleStartRecording}
                      disabled={hasAudio && !replacingAudio}
                    >
                      <Icon name="circle" />
                      Start Recording
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      icon
                      labelPosition="left"
                      onClick={handleStopRecording}
                    >
                      <Icon name="stop" />
                      Stop Recording
                    </Button>
                  )}

                  {isRecording && (
                    <div className="recording-status recording">
                      <Icon name="circle" color="red" className='recording-dot-icon' />
                      Recording... {formatDuration(recordingDuration)}
                    </div>
                  )}

                  {audioURL && !isRecording && (
                    <Button
                      type="button"
                      color="red"
                      basic
                      icon
                      onClick={handleDeleteAudio}
                    >
                      <Icon name="trash" />
                      Delete Recording
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {audioURL && !isRecording && audioSource === 'recording' && (
              <div className="audio-player-section">
                <Label>
                  <Icon name="music" />
                  Recorded Audio
                </Label>
                <audio controls src={audioURL} style={{ width: '100%', marginTop: '10px' }} />
                {recordingDuration > 0 && (
                  <div className="duration-display">
                    <Icon name="clock" />
                    Duration: {formatDuration(recordingDuration)}
                  </div>
                )}
              </div>
            )}
          </Segment>

          {/* OR Divider */}
          {!hasAudio && (
            <div className="audio-source-divider" style={{
              textAlign: 'center',
              margin: '20px 0',
              fontWeight: '600',
              color: '#666',
              fontSize: '1.2em'
            }}>
              — OR —
            </div>
          )}

          {/* Upload Audio File Section */}
          <Segment>
            <h4>
              <Icon name="upload" />
              Upload Audio File
            </h4>

            <div
              {...getRootProps()}
              className={`audio-upload-dropzone ${isDragActive ? 'drag-active' : ''} ${hasAudio ? 'disabled' : ''}`}
              style={{
                marginTop: '15px',
                padding: '30px',
                border: '2px dashed #ccc',
                borderRadius: '8px',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                backgroundColor: isDragActive ? '#f0f8ff' : 'transparent',
                borderColor: isDragActive ? '#2185d0' : '#ccc',
                cursor: hasAudio ? 'not-allowed' : 'pointer',
                opacity: hasAudio ? '0.5' : '1'
              }}
            >
              <input {...getInputProps()} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <Image src={microphoneAndFile} size="small" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '1.1em', marginTop: '0', marginBottom: '10px' }}>
                    {isDragActive
                      ? 'Drop the audio file here...'
                      : 'Drag & drop an audio file here, or click to browse'}
                  </p>
                  <p style={{ fontSize: '0.9em', color: '#888', marginTop: '0', marginBottom: '5px' }}>
                    Accepted formats: MP3, WAV, OGG, M4A, WEBM (Max 20MB), MOV* (Max 500MB)
                  </p>
                  <p style={{ fontSize: '0.8em', color: '#aaa', marginTop: '0', marginBottom: '0' }}>
                    *MOV file support depends on browser compatibility
                  </p>
                </div>
              </div>
            </div>

            {audioURL && audioSource === 'upload' && (
              <div className="audio-player-section" style={{ marginTop: '20px' }}>
                <Label>
                  <Icon name="file audio" />
                  Uploaded Audio: {uploadedAudioName}
                </Label>
                {uploadedAudioSize > 0 && (
                  <div style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                    <Icon name="server" />
                    File size: {formatFileSize(uploadedAudioSize)}
                  </div>
                )}
                <audio controls src={audioURL} style={{ width: '100%', marginTop: '10px' }} />
                {recordingDuration > 0 && (
                  <div className="duration-display">
                    <Icon name="clock" />
                    Duration: {formatDuration(recordingDuration)}
                  </div>
                )}
                <Button
                  type="button"
                  color="red"
                  basic
                  icon
                  onClick={handleDeleteAudio}
                  style={{ marginTop: '10px' }}
                >
                  <Icon name="trash" />
                  Delete Audio
                </Button>
              </div>
            )}
          </Segment>
        </>
      )}
    </div>
  );
};

AudioRecorderSection.propTypes = {
  audioManager: PropTypes.shape({
    state: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
    helpers: PropTypes.object.isRequired,
    computed: PropTypes.object.isRequired
  }).isRequired,
  isCreatingNew: PropTypes.bool,
  onAudioChange: PropTypes.func
};

export default AudioRecorderSection;
